import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CardHeader } from '../ui/card';
import { Switch } from '../ui/switch';

interface NotificationCardHeaderProps {
  title: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  /** Prefix for the `data-testid`s of the collapse toggle, label and switch. */
  testIdPrefix: string;
  /** Tooltip for the enable/disable control; defaults to the shared hint copy. */
  enabledTitle?: string;
}

/**
 * Standardized header shared by every notification card: a chevron button that
 * collapses/expands the card body plus an enable/disable switch. Presentational
 * — both the expanded and enabled states are owned by the containing card.
 */
export const NotificationCardHeader: React.FC<NotificationCardHeaderProps> = ({
  title,
  expanded,
  onToggleExpanded,
  enabled,
  onEnabledChange,
  testIdPrefix,
  enabledTitle,
}) => {
  const { t } = useTranslation('admin');

  return (
    <CardHeader>
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          data-testid={`${testIdPrefix}-collapse-toggle`}
        >
          {expanded ? (
            <ChevronDown className="h-5 w-5 shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0" />
          )}
          <span className="text-2xl font-semibold leading-none tracking-tight">{title}</span>
        </button>
        <label
          className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap"
          data-testid={`${testIdPrefix}-enabled-label`}
          title={enabledTitle ?? t('notificationTemplates.enabledHint')}
        >
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            data-testid={`${testIdPrefix}-enabled`}
          />
          {enabled ? t('notificationTemplates.enabled') : t('notificationTemplates.disabled')}
        </label>
      </div>
    </CardHeader>
  );
};
